import type { ChangeReason, Gender, GroupStatus, IhqType, MatchTier, MemberType, RatingSource, Relation, UserRole } from "./enums";
import type { GroupDetail, MemberAssignedRating, SalesCensus } from "./group";

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  /** True when the caller listed this status as expected and will handle it without a toast. */
  isExpected = false;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export class NotImplementedError extends Error {
  constructor(endpoint: string) {
    super(`${endpoint} is not available on the API yet.`);
    this.name = "NotImplementedError";
  }
}

export interface WorkbenchUser {
  id: string;
  displayName: string;
  email: string;
  role: UserRole;
}

export interface CurrentUser extends WorkbenchUser {
  recentGroupIds: readonly string[];
}

export interface PagedResult<T> {
  items: readonly T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface GroupSearchQuery {
  name?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  underwriterUserId?: string;
  quoteOwnerName?: string;
  opportunityId?: string;
  /** Superseded versions stay out of lists unless asked for (the Group Overview versions list asks). */
  includeSuperseded?: boolean;
  page?: number;
  pageSize?: number;
}

export interface OpportunitySummary {
  id: string;
  name: string;
  accountName: string;
  quoteOwnerName: string;
  /** Salesforce User id of the Quote owner, for the record link. */
  quoteOwnerUserId: string;
  effectiveDate: string;
  /** True when the Opportunity's record type is Renewal (the group was loaded from last year's membership). */
  isRenewal: boolean;
}

export interface GroupDraft {
  name: string;
  effectiveDate: string;
  ihqType: IhqType;
  notes: string | null;
  opportunityId: string;
  /** When set, members and assigned ratings are copied from this group (product plan 3.6). */
  copyOfGroupId?: string;
  /** Members parsed from a Census Creator workbook, committed with the group. */
  importedMembers?: readonly MemberDraft[];
}

export interface GroupPatch {
  name?: string;
  effectiveDate?: string;
  ihqType?: IhqType;
  notes?: string | null;
  underwriterUserId?: string | null;
  uwOverrideMultiplier?: number;
}

export interface SkynetAssignedRatingPayload {
  ratingId: number | null;
  conditionDescription: string;
  ratingText: string;
  time: string | null;
  debits: number;
  excluded: boolean;
  notes: string | null;
  irixSource: boolean;
  source: RatingSource;
}

export interface SkynetMemberPayload {
  externalMemberId: string | null;
  firstName: string;
  lastName: string;
  relation: Relation;
  subscriberExternalMemberId: string | null;
  gender: Gender | null;
  dateOfBirth: string | null;
  heightFeet: number | null;
  heightInches: number | null;
  weightLbs: number | null;
  postalCode: string | null;
  ssn: string | null;
  notes: string | null;
  excluded: boolean;
  memberType: MemberType;
  cohortYear: number | null;
  cohortGender: Gender | null;
  assignedRatings: readonly SkynetAssignedRatingPayload[];
}

/** POST /groups body used by Skynet's publish step (product plan 7.2). */
export interface CreateGroupRequest {
  externalKey: string;
  source: "Skynet" | "Manual";
  opportunityId: string;
  groupName: string;
  effectiveDate: string;
  ihqType: IhqType;
  notes: string | null;
  members: readonly SkynetMemberPayload[];
}

export interface CreateGroupResponse {
  groupId: string;
  /** Skynet's read-only version behind the working copy; null for a manual create. */
  skynetVersionGroupId: string | null;
  groupUrl: string;
  status: string;
  created: boolean;
  totals: {
    employees: number;
    spouses: number;
    children: number;
    totalMembers: number;
    membersExcluded: number;
    totalDebits: number;
    rmm: number;
    adjustedRmm: number;
  };
  members: readonly { externalMemberId: string | null; memberId: string; totalDebits: number; buildPoints: number; rxDebits: number; conditionDebits: number }[];
  warnings: readonly string[];
}

export interface MemberDraft {
  firstName: string;
  lastName: string;
  relation: Relation;
  subscriberMemberId: string | null;
  gender: Gender | null;
  dateOfBirth: string | null;
  heightFeet: number | null;
  heightInches: number | null;
  weightLbs: number | null;
  postalCode: string | null;
  ssn: string | null;
  notes: string | null;
  memberType: MemberType;
  externalMemberId?: string | null;
}

export interface MemberPatch {
  firstName?: string;
  lastName?: string;
  relation?: Relation;
  subscriberMemberId?: string | null;
  gender?: Gender | null;
  dateOfBirth?: string | null;
  heightFeet?: number | null;
  heightInches?: number | null;
  weightLbs?: number | null;
  postalCode?: string | null;
  ssn?: string | null;
  notes?: string | null;
  isReviewed?: boolean;
  irixChecked?: boolean;
  confirmedValidAuth?: boolean;
}

export interface ToggleMemberExcludedResult {
  detail: GroupDetail;
  autoExcludedSpockMemberIds: readonly string[];
}

export interface AssignedRatingSelection {
  ratingId: number;
  debitsOverride: number | null;
  uwDollars: number | null;
  irixSource: boolean;
  notes: string | null;
}

export interface AddAssignedRatingsRequest {
  selections: readonly AssignedRatingSelection[];
  changeReason: ChangeReason | null;
  changeExplanation: string | null;
}

export interface BrandGenericSubstitution {
  brandRatingId: number;
  brandName: string;
  genericRatingId: number;
  genericName: string;
  debits: number;
}

export interface AddAssignedRatingsResult {
  detail: GroupDetail;
  created: readonly MemberAssignedRating[];
  /** Ratings the member already held; override, dollars, IRIX flag, and notes were updated in place. */
  updated: readonly MemberAssignedRating[];
  autoAdded: readonly MemberAssignedRating[];
  substitutions: readonly BrandGenericSubstitution[];
}

/** PATCH .../members/{id}/flags: the member fields that cannot change a debit, saved without the engine. At least one must be set. */
export interface MemberFlagsPatch {
  irixChecked?: boolean;
  confirmedValidAuth?: boolean;
}

/** What a flag save changes beyond the flag: any underwriter work moves an unworked group into PreScreen in progress. */
export interface FlagChangeResult {
  groupStatus: GroupStatus;
}

export interface AssignedRatingPatch {
  assignedDebits?: number;
  uwDollars?: number | null;
  notes?: string | null;
  isExcluded?: boolean;
  irixSource?: boolean;
  changeReason?: ChangeReason | null;
  changeExplanation?: string | null;
}

export interface SendToSalesResult {
  detail: GroupDetail;
  /** What Salesforce received; also stored on the version as `sentCensus`. */
  census: SalesCensus;
  supersededGroupIds: readonly string[];
}

/** Sales-side what-if: which census lines are still enrolling. */
export interface RmmPreviewRequest {
  includedCensusMemberIds: readonly string[];
}

export interface RmmPreview {
  /** The figures as sent and the workbench's review guardrail; Salesforce's census review reads these, the workbench UI does not yet. */
  sentRmm?: number;
  sentAdjustedRmm?: number;
  sentMembers?: number;
  rmmChangePercent?: number;
  requiresUnderwriterReview?: boolean;
  reviewReasons?: string[];
  rmm: number;
  adjustedRmm: number;
  totalDebits: number;
  includedMembers: number;
}

/** Reopen creates a new working version; the completed version stays as sent. */
export interface ReopenGroupResult {
  detail: GroupDetail;
  previousVersionGroupId: string;
}

/** How an older version is brought forward: overwrite the latest working version, or add the next one. */
export type RestoreVersionMode = "replaceLatest" | "newVersion";

export interface RestoreVersionRequest {
  mode: RestoreVersionMode;
}

export interface RestoreVersionResult {
  /** The version the underwriter now works on. */
  detail: GroupDetail;
  sourceGroupId: string;
  /** Set when an existing version was overwritten instead of a new one created. */
  replacedGroupId: string | null;
}

export interface GroupHistorySummary {
  previouslyUnderwrittenCount: number;
  previouslySold: boolean;
  mostRecentRmm: number | null;
  mostRecentGroupId: string | null;
  mostRecentGroupName: string | null;
}

export interface PriorYearRating {
  conditionName: string;
  ratingText: string;
  debits: number;
  planYear: string;
}

export interface CensusImportRow {
  rowNumber: number;
  name: string;
  externalId: string | null;
  relation: string;
  gender: string | null;
  dateOfBirth: string | null;
  heightFeet: number | null;
  heightInches: number | null;
  weightLbs: number | null;
  ssn: string | null;
  notes: string | null;
  errors: readonly string[];
}

export interface CensusImportPreview {
  fileName: string;
  rows: readonly CensusImportRow[];
}

export interface ConditionSearchHit {
  conditionId: number;
  name: string;
  category: string;
  isCustom: boolean;
  isActive: boolean;
  selectionCount: number;
  matchTier: MatchTier;
  matchedText: string | null;
}

export interface CatalogSearchResult {
  query: string;
  tiers: readonly { tier: MatchTier; hits: readonly ConditionSearchHit[] }[];
}

/** One condition the smart tier suggests; the rating the index matched is the evidence. */
export interface SmartConditionHit {
  conditionId: number;
  name: string;
  isCustom: boolean;
  matchedRatingText: string;
  indexHits: number;
}

export interface SmartSearchResult {
  query: string;
  hits: readonly SmartConditionHit[];
  unmappedIndexConditions: number;
  timings: { embeddingMs: number; searchMs: number; mappingMs: number; totalMs: number };
}

export interface RatingWithEffectiveDebits {
  ratingId: number;
  conditionId: number;
  ratingText: string;
  time: string | null;
  baseDebits: number;
  effectiveDebits: number;
  modification: { multiplier: number | null; overrideDebits: number | null; notes: string } | null;
  isMedication: boolean;
  isCustom: boolean;
  isActive: boolean;
  notes: string | null;
  selectionCount: number;
  /** FDA-approved indications recorded for a medication rating. */
  indications: readonly string[];
  genericSubstitute: { ratingId: number; ratingText: string; effectiveDebits: number } | null;
  specialtyDrug: { drugName: string; isPapEligible: boolean } | null;
}

export interface ConditionDraft {
  name: string;
  notes: string | null;
}

export interface ConditionPatch {
  name?: string;
  notes?: string | null;
  description?: string | null;
  developmentQuestions?: string | null;
  isActive?: boolean;
  /** Plain-language underwriting rules shown on Select Rating; allowed on guideline conditions too. */
  underwritingRuleMarkdown?: string | null;
}

export interface RatingDraft {
  ratingText: string;
  time: string | null;
  debits: number;
  notes: string | null;
  isMedication: boolean;
}

export interface RatingPatch {
  ratingText?: string;
  time?: string | null;
  baseDebits?: number;
  notes?: string | null;
  isActive?: boolean;
}

export interface DebitModificationDraft {
  multiplier: number | null;
  overrideDebits: number | null;
  notes: string;
}

export type ModificationReportRowKind = "Custom Condition" | "Custom Rating" | "Debit Modification";

/** GET /catalog/modifications: one page of the customizations table. `pageSize` 0 returns every matching row (the export). */
export interface ModificationReportQuery {
  kind?: ModificationReportRowKind;
  /** Matches the condition, rating, detail, or author, case-insensitively. */
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface ModificationReportRow {
  id: string;
  kind: ModificationReportRowKind;
  conditionId: number;
  conditionName: string;
  /** Null for a Custom Condition row. */
  ratingId: number | null;
  ratingText: string | null;
  detail: string;
  authorName: string;
  createdUtc: string;
}

/** Where a MUGS manual release came from. Milliman uploads and the exported EDW archives arrive through the same screen. */
export type MugsManualSource = "Milliman" | "WebMugsArchive" | "CurrentCatalogSeed" | "CustomSeed";

export type MugsManualStatus = "Staged" | "Applied" | "Discarded";

/** One difference the importer found between a release and the catalog; the unit the importer approves or ignores. */
export type MugsManualChangeKind =
  | "NewCondition"
  | "NewRating"
  | "RemovedCondition"
  | "RemovedRating"
  | "DebitsChanged"
  | "NotesChanged"
  | "CodeChanged"
  | "Renamed"
  | "TimeChanged"
  | "TimeBandsSplit"
  | "DuplicateSourceRows";

export type MugsManualChangeDecision = "Approve" | "Ignore";

/** A release in the history list: one row per Milliman delivery, archived release, or seed. */
export interface MugsManualRelease {
  id: string;
  name: string;
  effectiveFrom: string | null;
  source: MugsManualSource;
  status: MugsManualStatus;
  isCurrent: boolean;
  loadedUtc: string;
  loadedByUserId: string;
  appliedUtc: string | null;
  appliedByUserId: string | null;
  conditions: number;
  ratings: number;
  changes: number;
  ignored: number;
}

export interface MugsManualChangeCount {
  kind: MugsManualChangeKind;
  total: number;
  ignored: number;
}

/** The preview header for a staged release. */
export interface MugsManualChangeSummary {
  mugsManualId: string;
  name: string;
  status: MugsManualStatus;
  counts: readonly MugsManualChangeCount[];
  ignoredCount: number;
  warnings: readonly string[];
}

export interface MugsManualChange {
  id: string;
  mugsManualId: string;
  changeKind: MugsManualChangeKind;
  conditionId: number | null;
  ratingId: number | null;
  sourceCdesNo: number | null;
  sourceConNbr: number | null;
  conditionName: string;
  ratingText: string | null;
  time: string | null;
  oldValue: string | null;
  newValue: string | null;
  /** A debit override exists on this rating; it stays in place whatever the release says. */
  hasActiveModification: boolean;
  decision: MugsManualChangeDecision;
  decidedByUserId: string | null;
  decidedUtc: string | null;
}

export interface MugsManualApplyResult {
  mugsManualId: string;
  conditionsCreated: number;
  conditionsUpdated: number;
  conditionsRetired: number;
  ratingsCreated: number;
  ratingsUpdated: number;
  ratingsRetired: number;
  overridesCreated: number;
  changesApplied: number;
  changesIgnored: number;
  warnings: readonly string[];
}

/** What the upload screen sends: the two Milliman CSV files and the release's name. */
export interface MugsManualUploadDraft {
  name: string;
  effectiveFrom: string | null;
  notes: string | null;
  conditionFile: File;
  ratingFile: File;
}
